import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { FormContainer } from '@eika-kortbetjening/form-container';
import { Input } from '@eika/input';
import { RadioButtonPanels } from '@eika/radio-button';

import closeCreditAccountRoutes from '@components/close-credit-account/routes';
import Footer from '@components/footer/Footer';
import useText from '@hooks/use-text';

import { getCloseCreditAccountUrl } from '../functions';

import './CloseCreditAccountFeedback.scss';

const CloseCreditAccountFeedback: React.FC = () => {
    const navigate = useNavigate();
    const { worthKnowingUrl } = closeCreditAccountRoutes;

    const {
        formPleaseFillOutForm,
        formPleaseFillOutAlternative,
        closeCreditAccountFeedbackOption1,
        closeCreditAccountFeedbackOption2,
        closeCreditAccountFeedbackOption3,
        closeCreditAccountFeedbackOption4,
        closeCreditAccountFeedbackOptionOther,
        closeCreditAccountFeedbackInputPlaceholder,
        closeCreditAccountFeedbackTitle,
    } = useText();

    const [queryParams] = useSearchParams();
    const reason = queryParams.get('reason');

    const [selectedOption, setSelectedOption] = React.useState<string>(reason || '');
    const [value, setValue] = React.useState<string>('');
    const [error, setError] = useState<string | undefined>();

    const next = () => {
        if (!value.trim() && selectedOption === closeCreditAccountFeedbackOptionOther) {
            setError(formPleaseFillOutForm);
            return;
        }
        if (!selectedOption) {
            setError(formPleaseFillOutAlternative);
            return;
        }

        const reason =
            selectedOption === closeCreditAccountFeedbackOptionOther ? `${selectedOption}: ${value}` : selectedOption;
        queryParams.set('reason', reason);

        navigate(getCloseCreditAccountUrl(queryParams, worthKnowingUrl, queryParams.toString()));
    };

    const handleOtherChange = (input: string) => {
        setValue(input);
        if (!input) {
            setError(formPleaseFillOutForm);
        } else {
            setError(undefined);
        }
    };

    const handleChangeOption = (newOption: string) => {
        setSelectedOption(newOption);
        if (newOption !== closeCreditAccountFeedbackOptionOther) {
            setError(undefined);
        }
    };

    useEffect(() => {
        if (selectedOption !== closeCreditAccountFeedbackOptionOther && value !== '') {
            setValue('');
        }
    }, [closeCreditAccountFeedbackOptionOther, selectedOption, value]);

    return (
        <FormContainer>
            <FormContainer.Content>
                <RadioButtonPanels
                    label={closeCreditAccountFeedbackTitle}
                    name="radio-button-panels"
                    onChange={handleChangeOption}
                    selectedOption={selectedOption}
                    errorMessage={error === formPleaseFillOutForm ? undefined : error}
                    options={[
                        { value: closeCreditAccountFeedbackOption1 },
                        { value: closeCreditAccountFeedbackOption2 },
                        { value: closeCreditAccountFeedbackOption3 },
                        { value: closeCreditAccountFeedbackOption4 },
                        { value: closeCreditAccountFeedbackOptionOther },
                    ]}
                />
                {selectedOption === closeCreditAccountFeedbackOptionOther && (
                    <Input
                        inputMode="text"
                        className="close-credit-account-other-input"
                        value={value}
                        placeholder={closeCreditAccountFeedbackInputPlaceholder}
                        maxLength={35}
                        onChange={(event) => handleOtherChange(event.target.value)}
                        error={error === formPleaseFillOutAlternative ? undefined : error}
                    />
                )}
            </FormContainer.Content>
            <FormContainer.Footer>
                <Footer next={next} disabled={!!error} />
            </FormContainer.Footer>
        </FormContainer>
    );
};

export default CloseCreditAccountFeedback;
